import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Divider,
  IconButton,
  alpha,
} from '@mui/material';
import {
  AccountTree,
  Gavel,
  Psychology,
  Shield,
  TrendingUp,
  VerifiedUser,
  Visibility,
  Article,
  Security,
  AccountBalance,
  Assessment,
  Groups,
  Approval,
  Calculate,
  ArrowForward,
  CheckCircle,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import DemoLoginModal from '../components/auth/DemoLoginModal';

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinks = [
    { label: 'Platform', id: 'platform' },
    { label: 'Capabilities', id: 'capabilities' },
    { label: 'Compliance', id: 'compliance' },
    { label: 'Architecture', id: 'architecture' },
  ];

  const platformPillars = [
    {
      icon: <AccountBalance sx={{ fontSize: 32 }} />,
      title: 'Fund Operations',
      subtitle: 'Share classes, NAV workflow, dealing days',
      items: [
        'Multi-share-class fund structures (institutional, retail, hedged)',
        'NAV strike pipeline: calculate, review, override, approve, publish',
        'Dealing day management with T+0 to T+3 settlement cycles',
        'Cut-off time enforcement with Luxembourg business calendar',
      ],
    },
    {
      icon: <Approval sx={{ fontSize: 32 }} />,
      title: 'Four-Eyes Controls',
      subtitle: 'Maker-checker on every critical operation',
      items: [
        'Configurable approval chains per operation type',
        'Segregation of duties enforcement',
        'NAV approval, investor onboarding, regulatory filings',
        'Complete audit trail with approver, timestamp, rationale',
      ],
    },
    {
      icon: <Groups sx={{ fontSize: 32 }} />,
      title: 'Investor Register',
      subtitle: 'KYC-gated onboarding and position tracking',
      items: [
        'Investor lifecycle: prospect, KYC review, approved, active',
        'Qualified investor eligibility gating (SIF/RAIF)',
        'Beneficial ownership tracking',
        'Position management across share classes',
      ],
    },
    {
      icon: <TrendingUp sx={{ fontSize: 32 }} />,
      title: 'Transaction Engine',
      subtitle: 'Subscriptions, redemptions, switches, settlements',
      items: [
        'Subscription and redemption processing with NAV-based pricing',
        'Dealing day validation and settlement date calculation',
        'Switch transactions between share classes',
        'Settlement workflow with confirmation tracking',
      ],
    },
  ];

  const capabilities = [
    {
      icon: <Calculate />,
      title: 'NAV Calculation & Verification',
      description: 'Pricing source hierarchy with depositary verification. SHA-256 hash chain ensures immutable NAV history. Override workflow with mandatory reason codes.',
      badge: 'CSSF Circular 02/77',
    },
    {
      icon: <Assessment />,
      title: 'Risk Analytics Engine',
      description: 'VaR (historical, parametric, Monte Carlo) at 95%/99% confidence. Expected shortfall, correlation matrices, Herfindahl concentration index, stress testing across 6 scenarios.',
      badge: 'UCITS Risk Limits',
    },
    {
      icon: <Article />,
      title: 'KID/KIID Generation',
      description: 'PRIIPs-compliant Key Information Documents generated from real fund data. Summary Risk Indicator (1-7), performance scenarios, cost disclosure with Reduction in Yield.',
      badge: 'PRIIPs (EU) 1286/2014',
    },
    {
      icon: <Shield />,
      title: 'Depositary Oversight',
      description: 'Cash flow monitoring, safekeeping reconciliation, NAV verification with configurable tolerance. Quarterly reporting with findings aggregation.',
      badge: 'AIFMD Art. 21',
    },
    {
      icon: <Security />,
      title: 'AML/KYC Screening',
      description: 'Investor screening against sanctions lists with fuzzy name matching. Match scoring, false-positive resolution, periodic rescreening schedules.',
      badge: 'CSSF AML/CFT',
    },
    {
      icon: <Gavel />,
      title: 'Regulatory Change Monitoring',
      description: 'Real-time tracking of regulatory updates from CSSF, ESMA, BaFin, AMF, FSMA, EBA. AI-powered impact analysis and deadline extraction.',
      badge: 'Multi-Jurisdiction',
    },
    {
      icon: <Psychology />,
      title: 'AI Compliance Intelligence',
      description: 'Multi-provider AI integration (OpenAI, Azure, Anthropic) for document classification, compliance scoring, and predictive regulatory analysis.',
      badge: 'AI-Powered',
    },
    {
      icon: <AccountTree />,
      title: 'Fund Structure Visualization',
      description: 'Interactive network graphs of fund-service provider relationships. ManCo delegation oversight documentation with concentration risk mapping.',
      badge: 'ManCo Substance',
    },
    {
      icon: <Visibility />,
      title: 'Position Break Detection',
      description: 'Reconciliation break detection with severity classification and resolution workflow. Expected vs actual positions with assigned resolution tracking.',
      badge: 'Fund Ops',
    },
  ];

  const complianceFrameworks = [
    { name: 'UCITS V/VI', description: 'Share classes, risk limits, depositary, KIID' },
    { name: 'AIFMD / AIFMD II', description: 'Annex IV reporting, depositary oversight, leverage' },
    { name: 'PRIIPs', description: 'KID generation, SRI calculation, cost disclosure' },
    { name: 'CSSF', description: 'Audit trail, four-eyes, on-site readiness' },
    { name: 'MiFID II', description: 'Transaction reporting, best execution, suitability' },
    { name: 'AML/CFT', description: 'KYC screening, rescreening, 10-year retention' },
    { name: 'FATCA/CRS', description: 'Self-certification, reporting obligations' },
    { name: 'GDPR', description: 'EU data residency, retention policies, access controls' },
  ];

  const architectureStats = [
    { value: '267', label: 'API Endpoints', detail: 'RESTful, authenticated' },
    { value: '75+', label: 'Database Tables', detail: '16 versioned migrations' },
    { value: '32', label: 'Dashboard Pages', detail: 'Fund ops, compliance, analytics' },
    { value: 'RS256', label: 'JWT Authentication', detail: 'Token rotation, TOTP 2FA' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: alpha('#0a0f0a', 0.95),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ maxWidth: 1200, width: '100%', mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <AccountBalance sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                color: theme.palette.text.primary,
                letterSpacing: '-0.02em',
              }}
            >
              AmudFin
            </Typography>
            <Chip
              label="LUXEMBOURG"
              size="small"
              sx={{
                ml: 1,
                height: 20,
                fontSize: '0.6rem',
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 600,
                letterSpacing: '0.1em',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              }}
            />
          </Box>
          {!isMobile && (
            <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
              {navLinks.map((link) => (
                <Button
                  key={link.id}
                  size="small"
                  onClick={() => scrollToSection(link.id)}
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    '&:hover': { color: theme.palette.text.primary },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              onClick={() => setDemoModalOpen(true)}
              sx={{
                color: theme.palette.text.secondary,
                borderColor: theme.palette.divider,
                fontSize: '0.8rem',
              }}
              variant="outlined"
            >
              Demo
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ fontSize: '0.8rem' }}
            >
              Sign In
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box
        sx={{
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle grid background */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Box sx={{ maxWidth: 800 }}>
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.primary.main,
                mb: 2,
                display: 'block',
                fontSize: '0.75rem',
                letterSpacing: '0.15em',
              }}
            >
              Fund Management Intelligence Platform
            </Typography>

            <Typography
              variant={isMobile ? 'h3' : 'h1'}
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 3,
                lineHeight: 1.1,
                fontSize: { xs: '2rem', md: '3rem', lg: '3.5rem' },
                fontFamily: '"JetBrains Mono", monospace',
              }}
            >
              Operational infrastructure{' '}
              <Box
                component="span"
                sx={{ color: theme.palette.primary.main }}
              >
                for Luxembourg funds
              </Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                mb: 4,
                maxWidth: 620,
                lineHeight: 1.6,
                fontWeight: 400,
                fontFamily: '"Inter", sans-serif',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
              }}
            >
              Share class management, NAV strike workflows, four-eyes controls,
              investor register, and transaction processing &mdash; built for
              ManCos, AIFMs, and fund administrators operating under UCITS,
              AIFMD, and CSSF supervision.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 6 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                endIcon={<ArrowForward />}
                sx={{ px: 4, py: 1.5 }}
              >
                Access Platform
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setDemoModalOpen(true)}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                Try Demo Environment
              </Button>
            </Stack>

            {/* Regulatory framework chips */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
              {['UCITS V/VI', 'AIFMD II', 'PRIIPs', 'CSSF', 'MiFID II', 'AML/CFT'].map((fw) => (
                <Chip
                  key={fw}
                  label={fw}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: alpha(theme.palette.text.secondary, 0.2),
                    color: theme.palette.text.secondary,
                    fontSize: '0.7rem',
                    fontFamily: '"JetBrains Mono", monospace',
                    height: 24,
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Scroll indicator */}
          <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 8 } }}>
            <IconButton
              onClick={() => scrollToSection('platform')}
              sx={{ color: theme.palette.text.secondary, animation: 'bounce 2s infinite' }}
            >
              <KeyboardArrowDown />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* Platform Pillars */}
      <Box
        id="platform"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: 'background.paper',
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: theme.palette.primary.main, mb: 1, display: 'block' }}>
            Core Operations
          </Typography>
          <Typography variant="h2" sx={{ mb: 1, fontWeight: 700 }}>
            Fund operations, not dashboards
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.text.secondary, mb: 6, maxWidth: 600 }}
          >
            Every workflow maps to how Luxembourg fund operations teams actually work &mdash;
            dealing days, settlement cycles, cut-off times, four-eyes controls.
          </Typography>

          <Grid container spacing={3}>
            {platformPillars.map((pillar, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.4) },
                  }}
                >
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: 'flex',
                        }}
                      >
                        {pillar.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          {pillar.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          {pillar.subtitle}
                        </Typography>
                      </Box>
                    </Box>
                    <Stack spacing={1.5}>
                      {pillar.items.map((item, j) => (
                        <Box key={j} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <CheckCircle
                            sx={{
                              fontSize: 16,
                              color: theme.palette.primary.main,
                              mt: 0.3,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Capabilities Grid */}
      <Box
        id="capabilities"
        sx={{ py: { xs: 8, md: 12 }, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: theme.palette.primary.main, mb: 1, display: 'block' }}>
            Platform Capabilities
          </Typography>
          <Typography variant="h2" sx={{ mb: 1, fontWeight: 700 }}>
            267 endpoints. 75+ tables. Zero mocks.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.text.secondary, mb: 6, maxWidth: 600 }}
          >
            Every feature is production-implemented with real database operations,
            real calculations, and real audit trails.
          </Typography>

          <Grid container spacing={2}>
            {capabilities.map((cap, i) => (
              <Grid item xs={12} sm={6} lg={4} key={i}>
                <Card
                  sx={{
                    height: '100%',
                    p: 2.5,
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.4) },
                  }}
                >
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Box
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          display: 'flex',
                        }}
                      >
                        {cap.icon}
                      </Box>
                      <Chip
                        label={cap.badge}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.6rem',
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 500,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.light,
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75, fontSize: '0.85rem' }}>
                      {cap.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem', lineHeight: 1.5 }}>
                      {cap.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Regulatory Compliance */}
      <Box
        id="compliance"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ color: theme.palette.primary.main, mb: 1, display: 'block' }}>
                Regulatory Surface
              </Typography>
              <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                Built for CSSF inspection readiness
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                Every operation generates an immutable audit entry: who did what, when,
                from where, with what data. Four-eyes controls enforce segregation of duties.
                Data retention policies align with regulatory minimums.
              </Typography>
              <Stack spacing={1.5}>
                {[
                  'Immutable audit logs with user, action, IP, timestamp, old/new values',
                  'Four-eyes approval on NAV, onboarding, filings, risk limit changes',
                  'Data retention: 10 years AML, 7 years transactions, 5 years MiFID',
                  'RS256 JWT with token rotation, TOTP 2FA, bcrypt hashing',
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <VerifiedUser
                      sx={{ fontSize: 16, color: theme.palette.primary.main, mt: 0.3, flexShrink: 0 }}
                    />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {complianceFrameworks.map((fw, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <Card
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        transition: 'border-color 0.2s',
                        '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.4) },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          color: theme.palette.primary.main,
                          mb: 0.5,
                        }}
                      >
                        {fw.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem', lineHeight: 1.3 }}
                      >
                        {fw.description}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Architecture / Technical Credibility */}
      <Box
        id="architecture"
        sx={{ py: { xs: 8, md: 12 }, borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: theme.palette.primary.main, mb: 1, display: 'block' }}>
            Technical Architecture
          </Typography>
          <Typography variant="h2" sx={{ mb: 1, fontWeight: 700 }}>
            Production-grade. No prototypes.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.text.secondary, mb: 6, maxWidth: 600 }}
          >
            FastAPI + React + PostgreSQL. Every endpoint is authenticated, every mutation
            is audited, every calculation is real.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 6 }}>
            {architectureStats.map((stat, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Box
                  sx={{
                    p: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {stat.detail}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Tech stack bar */}
          <Box
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.5),
            }}
          >
            <Grid container spacing={3}>
              {[
                { label: 'Backend', items: ['FastAPI', 'SQLAlchemy', 'Alembic', 'PyOTP', 'structlog'] },
                { label: 'Frontend', items: ['React 18', 'TypeScript', 'MUI v5', 'Recharts', 'Zustand'] },
                { label: 'Data', items: ['PostgreSQL', 'Alembic Migrations', 'JSONB', 'UUID PKs'] },
                { label: 'Security', items: ['RS256 JWT', 'TOTP 2FA', 'bcrypt', 'Rate Limiting', 'CSP Headers'] },
              ].map((col, i) => (
                <Grid item xs={6} md={3} key={i}>
                  <Typography
                    variant="overline"
                    sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}
                  >
                    {col.label}
                  </Typography>
                  <Stack spacing={0.5}>
                    {col.items.map((item, j) => (
                      <Typography
                        key={j}
                        variant="body2"
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.75rem',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 8, md: 10 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            Built for Luxembourg fund professionals
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 4, maxWidth: 500, mx: 'auto' }}>
            If your operations require UCITS compliance, CSSF audit readiness, or
            four-eyes controls on every critical decision &mdash; this is your platform.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              endIcon={<ArrowForward />}
              sx={{ px: 4, py: 1.5 }}
            >
              Access Platform
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setDemoModalOpen(true)}
              sx={{
                px: 4,
                py: 1.5,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              Try Demo
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: 'background.paper',
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Brand column */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountBalance sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: theme.palette.text.primary,
                  }}
                >
                  AmudFin
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2, maxWidth: 280, lineHeight: 1.6 }}>
                Operational infrastructure for Luxembourg fund management.
                Built for ManCos, AIFMs, and fund administrators.
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.6), display: 'block' }}>
                AmudFin S.A. &mdash; Luxembourg
              </Typography>
            </Grid>

            {/* Platform column */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography
                variant="overline"
                sx={{ color: theme.palette.text.secondary, mb: 2, display: 'block', fontSize: '0.65rem' }}
              >
                Platform
              </Typography>
              <Stack spacing={1}>
                {[
                  { label: 'Sign In', to: '/login' },
                  { label: 'Register', to: '/register' },
                  { label: 'About', to: '/about' },
                ].map((link) => (
                  <Typography
                    key={link.to}
                    component={RouterLink}
                    to={link.to}
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': { color: theme.palette.primary.main },
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>

            {/* Legal column */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography
                variant="overline"
                sx={{ color: theme.palette.text.secondary, mb: 2, display: 'block', fontSize: '0.65rem' }}
              >
                Legal
              </Typography>
              <Stack spacing={1}>
                {[
                  { label: 'Privacy Policy', to: '/privacy' },
                  { label: 'Terms of Service', to: '/terms' },
                  { label: 'Cookie Policy', to: '/cookies' },
                  { label: 'Legal Notice', to: '/legal' },
                ].map((link) => (
                  <Typography
                    key={link.to}
                    component={RouterLink}
                    to={link.to}
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': { color: theme.palette.primary.main },
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>

            {/* Compliance column */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography
                variant="overline"
                sx={{ color: theme.palette.text.secondary, mb: 2, display: 'block', fontSize: '0.65rem' }}
              >
                Compliance
              </Typography>
              <Stack spacing={1}>
                {[
                  { label: 'Data Processing', to: '/data-processing' },
                  { label: 'Accessibility', to: '/accessibility' },
                  { label: 'GDPR Rights', to: '/privacy#rights' },
                ].map((link) => (
                  <Typography
                    key={link.to}
                    component={RouterLink}
                    to={link.to}
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': { color: theme.palette.primary.main },
                    }}
                  >
                    {link.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>

            {/* Contact column */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography
                variant="overline"
                sx={{ color: theme.palette.text.secondary, mb: 2, display: 'block', fontSize: '0.65rem' }}
              >
                Contact
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                  Luxembourg City
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                  info@amudfin.com
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
                  DPO: dpo@amudfin.com
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Bottom bar */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.5) }}>
              &copy; {new Date().getFullYear()} AmudFin S.A. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
              {['GDPR', 'ePrivacy', 'CNPD', 'EU Data Residency'].map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.55rem',
                    fontFamily: '"JetBrains Mono", monospace',
                    borderColor: alpha(theme.palette.text.secondary, 0.15),
                    color: alpha(theme.palette.text.secondary, 0.5),
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Bounce animation */}
      <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-8px); }
          60% { transform: translateY(-4px); }
        }
      `}</style>

      <DemoLoginModal open={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </Box>
  );
};

export default LandingPage;
